// 📁 src/RegisterPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUser } from "./UserContext";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { toast } from "react-toastify";

// ✅ Same desktop background
import registerBgDesktop from "./assets/login-bg-desktop.png";

// ✅ Modern input class
const INPUT =
  "w-full rounded-2xl bg-[#FAFAF5] px-4 py-3.5 text-[15px] " +
  "border border-transparent shadow-sm " +
  "placeholder:text-[#1E5631]/45 " +
  "focus:bg-white focus:border-[#1E5631]/20 focus:ring-2 focus:ring-[#1E5631]/15 " +
  "outline-none transition";

const normalizePhone = (p) => String(p || "").replace(/\s+/g, "").trim();
const onlyDigitsMax6 = (v) => String(v || "").replace(/\D/g, "").slice(0, 6);

const RegisterPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);

  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const { register, sendOtp, verifyOtpOnly } = useUser();
  const navigate = useNavigate();

  const cleanPhone = useMemo(() => normalizePhone(phone), [phone]);

  // cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // reset OTP flow when phone changes
  useEffect(() => {
    setPhoneVerified(false);
    setOtpSent(false);
    setOtp("");
    setCooldown(0);
  }, [cleanPhone]);

  const handleSendOtp = async () => {
    if (!cleanPhone) return toast.error("Please enter phone number");
    if (phoneVerified) return;

    setLoadingSend(true);
    try {
      await sendOtp(cleanPhone, "register");
      setOtpSent(true);
      setCooldown(30);
      toast.success("✅ OTP sent");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoadingSend(false);
    }
  };

  const handleVerifyOtp = async () => {
    const cleanOtp = onlyDigitsMax6(otp);

    if (!cleanPhone || cleanOtp.length !== 6) {
      return toast.error("Please enter 6-digit OTP");
    }

    setLoadingVerify(true);
    try {
      await verifyOtpOnly(cleanPhone, cleanOtp, "register");
      setPhoneVerified(true);
      toast.success("✅ Phone verified");
    } catch (err) {
      toast.error(err?.response?.data?.message || "OTP verification failed");
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!phoneVerified) {
      toast.error("Please verify OTP first");
      return;
    }

    try {
      await register(fullName, cleanPhone, email, password);
      toast.success("✅ Registration successful!");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Registration failed");
    }
  };

  const canRegister =
    phoneVerified &&
    fullName.trim().length > 1 &&
    email.trim().length > 5 &&
    password.length >= 6;

  return (
    <div className="min-h-screen bg-[#FAFAF5] text-[#1E5631] relative overflow-hidden">
      {/* ✅ Desktop background */}
      <div
        className="hidden lg:block absolute inset-0"
        style={{
          backgroundImage: `url(${registerBgDesktop})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* ✅ Desktop overlay */}
      <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-transparent via-[#FAFAF5]/70 to-[#FAFAF5]/95" />

      {/* ✅ Mobile accents */}
      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#1E5631]/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-[#D4AF57]/12 blur-3xl" />
      </div>

      <div className="relative min-h-screen flex items-center justify-center lg:justify-end px-4 py-10 lg:pr-16">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] p-6 sm:p-10">
            {/* Mobile header */}
            <div className="lg:hidden mb-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-[#1E5631] text-white flex items-center justify-center font-bold">
                  NF
                </div>
                <div>
                  <div className="text-xl font-semibold">The Nutrafox</div>
                  <div className="text-xs text-[#1E5631]/70">
                    Sourcing Nature. Shipping Purity
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-6">
              Create Account
            </h2>

            <form onSubmit={handleRegister}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Full name
                </label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  className={INPUT}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>

              {/* PHONE + OTP */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Phone</label>

                <div className="rounded-2xl bg-[#FAFAF5] border border-transparent shadow-sm px-3 py-2.5 focus-within:bg-white focus-within:border-[#1E5631]/20 focus-within:ring-2 focus-within:ring-[#1E5631]/15 transition">
                  <PhoneInput
                    international
                    defaultCountry="IN"
                    value={phone}
                    onChange={(v) => setPhone(v || "")}
                    placeholder="Enter phone number"
                    className="phoneInput"
                    disabled={phoneVerified}
                  />
                </div>

                {/* actions */}
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-xs">
                    {phoneVerified ? (
                      <span className="text-green-700 font-semibold">
                        ✅ Verified
                      </span>
                    ) : otpSent ? (
                      <span className="text-[#1E5631]/70">
                        OTP sent. Verify to continue.
                      </span>
                    ) : (
                      <span className="text-[#1E5631]/55">
                        Verify OTP to enable Register.
                      </span>
                    )}
                  </div>

                  {phoneVerified ? (
                    <button
                      type="button"
                      className="text-xs font-semibold underline"
                      onClick={() => {
                        setPhoneVerified(false);
                        setOtpSent(false);
                        setOtp("");
                        setCooldown(0);
                      }}
                    >
                      Change number
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={loadingSend || cooldown > 0 || !cleanPhone}
                      className="text-xs font-semibold underline disabled:opacity-50"
                    >
                      {loadingSend
                        ? "Sending..."
                        : cooldown > 0
                        ? `Resend in ${cooldown}s`
                        : otpSent
                        ? "Resend OTP"
                        : "Send OTP"}
                    </button>
                  )}
                </div>

                {!phoneVerified && otpSent && (
                  <div className="mt-3 flex gap-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="Enter 6-digit OTP"
                      className={INPUT}
                      value={otp}
                      onChange={(e) => setOtp(onlyDigitsMax6(e.target.value))}
                    />
                    <button
                      type="button"
                      onClick={handleVerifyOtp}
                      disabled={loadingVerify || onlyDigitsMax6(otp).length !== 6}
                      className="rounded-2xl bg-[#1E5631] text-white px-4 py-3 font-semibold hover:bg-[#236e47] transition-all disabled:opacity-60"
                    >
                      {loadingVerify ? "Verifying..." : "Verify"}
                    </button>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  placeholder="you@company.com"
                  className={INPUT}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Password
                </label>
                <input
                  type="password"
                  placeholder="Create a strong password"
                  className={INPUT}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={!canRegister}
                className={
                  "w-full rounded-2xl py-3 font-semibold transition-all " +
                  (canRegister
                    ? "bg-[#1E5631] text-white hover:bg-[#236e47]"
                    : "bg-[#1E5631]/30 text-white")
                }
              >
                Register
              </button>
            </form>

            <p className="text-sm mt-6 text-center text-[#1E5631]/80">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold underline">
                Login
              </Link>
            </p>

            <div className="mt-6 text-[12px] text-center text-[#1E5631]/55">
              By continuing, you agree to our Terms & Privacy Policy.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
